import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import $ from "jquery"
import { breakpoints, onVisible } from "../lib/utils"

const sliderBreakpoint = 1400

export default class extends Controller<HTMLElement> {
  static values = {
    bookSignedId: String,
    prev: { type: String, default: "Previous page" },
    next: { type: String, default: "Next page" },
    initialSlide: { type: String, default: "0" },
    explanationContainer: { type: String, default: null },
  }

  declare readonly bookSignedIdValue: string
  declare prevValue: string
  declare nextValue: string
  declare initialSlideValue: string
  declare explanationContainerValue: string | null

  private previousSlideIndex: number | null = null

  connect() {
    if (window.innerWidth < sliderBreakpoint) {
      this.element.querySelectorAll(".slide-mobile-hide").forEach((el) => {
        el.remove()
      })
    }
    this.element.addEventListener("focusin", this.focusHandler)
    this.element.addEventListener("focusout", this.focusHandler)

    let initialSlideIndex = parseInt(this.initialSlideValue, 10)
    if (Number.isNaN(initialSlideIndex)) {
      initialSlideIndex = Math.max(
        0,
        $(this.element).find(".slide").index($(this.initialSlideValue)),
      )
    }

    onVisible(this.element, () => {
      $(this.element)
        .not(".slick-initialized")
        .slick({
          slidesToScroll: 2,
          slidesToShow: 2,
          initialSlide: initialSlideIndex,
          dots: false,
          arrows: true,
          infinite: false,
          prevArrow: `<button type="button" class="bg-primarygreen! slick-arrow slick-prev" title="${this.prevValue}"><span class="arrow-text">${this.prevValue}</span><i class="bi bi-chevron-left"></i></button>`,
          nextArrow: `<button type="button" class="bg-primarygreen! slick-arrow slick-next" title="${this.nextValue}"><span class="arrow-text">${this.nextValue}</span><i class="bi bi-chevron-right"></i></button>`,
          speed: 700,
          waitForAnimate: false,
          responsive: [
            {
              breakpoint: sliderBreakpoint,
              settings: {
                slidesToScroll: 1,
                slidesToShow: 1,
              },
            },
          ],
        })
        .on("afterChange", (_event, slick, currentSlide) => {
          const current_slider = $(slick.$slider).attr("id")

          if (
            !["book-slider-invite", "book-slider-cover", "book-slider-author"].includes(
              current_slider,
            )
          ) {
            return
          }

          const isAuthorOrCoverSlider = ["book-slider-author", "book-slider-cover"].includes(
            current_slider,
          )
          if (isAuthorOrCoverSlider) {
            if (this.previousSlideIndex !== null) {
              const leftSlide = slick.$slides[this.previousSlideIndex]
              if (
                leftSlide?.querySelector(
                  "#voting-choices-form, [data-slide-type^='two-sided-voting']",
                )
              ) {
                document.dispatchEvent(new CustomEvent("book-slider:saveVotingChoices"))
              }
              if (leftSlide?.querySelector("[data-slide-type^='two-sided-voting']")) {
                document.dispatchEvent(
                  new CustomEvent("participant:saveSelectionsBeforeSubmit", {
                    detail: { silent: true },
                  }),
                )
              }
              if (leftSlide?.querySelector("[data-slide-type^='statement-voting']")) {
                document.dispatchEvent(
                  new CustomEvent("book-slider:saveStatementVoting", {
                    detail: { leftSlide },
                  }),
                )
              }
              if (
                leftSlide?.querySelector("[data-slide-type^='statement-voting']") &&
                this.isLatestStatementVotingSlide(leftSlide)
              ) {
                document.dispatchEvent(
                  new CustomEvent("participant:saveSelectionsBeforeSubmit", {
                    detail: { silent: true },
                  }),
                )
              }
              if (leftSlide?.querySelector("[data-slide-type^='pie-voting']")) {
                document.dispatchEvent(
                  new CustomEvent("book-slider:savePieVoting", {
                    detail: { leftSlide },
                  }),
                )
              }
            }
            this.previousSlideIndex = currentSlide
          }

          if (this.explanationContainerValue) {
            this.changeExplanationPage()
          }

          const lastSlideActive =
            slick.$slides.length &&
            slick.$slides[slick.$slides.length - 1].classList.contains("slick-active")
          if (
            lastSlideActive &&
            current_slider === "book-slider-cover" &&
            window.innerWidth < breakpoints.md
          ) {
            document.querySelector(".btn-holder").scrollIntoView()
          }
        })
    })
  }

  changeExplanationPage() {
    const currentSlideWithType = this.element.querySelector(
      ".slick-current [data-slide-type]",
    ) as HTMLElement | null
    if (!currentSlideWithType) {
      return
    }

    let slideType = currentSlideWithType.dataset.slideType ?? ""
    if (slideType.startsWith("two-sided-voting")) {
      slideType = "two-sided-voting"
    }
    if (slideType.startsWith("statement-voting")) {
      slideType = "statement-voting"
    }
    if (slideType.startsWith("pie-voting")) {
      slideType = "pie-voting"
    }
    const explanationEl = document.querySelector(this.explanationContainerValue) as HTMLElement
    explanationEl.dataset.active = slideType
  }

  disconnect() {
    this.element.removeEventListener("focusin", this.focusHandler)
    this.element.removeEventListener("focusout", this.focusHandler)
    $(this.element).filter(".slick-initialized").slick("unslick")
  }

  focusHandler = (event: FocusEvent) => {
    const target = event.composedPath()[0]
    if (!(target instanceof Element) || !["INPUT", "TEXTAREA"].includes(target.tagName)) {
      return
    }

    if (event.type === "focusin") {
      $(this.element).slick("slickSetOption", "accessibility", false, false)
      $(this.element).slick("slickSetOption", "swipe", false, false)
    } else if (event.type === "focusout") {
      $(this.element).slick("slickSetOption", "accessibility", true, false)
      $(this.element).slick("slickSetOption", "swipe", true, false)
    }
  }

  findSlideIndex(element: HTMLElement) {
    const slideRoot = element.closest("[data-slick-index]") as HTMLElement | null
    if (!slideRoot) {
      throw new Error("Given element is not a child of a slide")
    }

    return parseInt(slideRoot.dataset.slickIndex, 10)
  }

  private isLatestStatementVotingSlide(slideEl: Element): boolean {
    const wrapper = slideEl.querySelector<HTMLElement>('[data-slide-type^="statement-voting"]')
    if (!wrapper) return false
    const slideType = wrapper.getAttribute("data-slide-type") ?? ""
    const wrappers = this.element.querySelectorAll<HTMLElement>(
      '[data-slide-type^="statement-voting"]',
    )
    let maxNum = 1
    for (const w of wrappers) {
      const type = w.getAttribute("data-slide-type") ?? ""
      const m =
        type === "statement-voting" ? 1 : parseInt(type.replace("statement-voting-", ""), 10)
      if (!Number.isNaN(m) && m > maxNum) maxNum = m
    }
    const latestType = maxNum === 1 ? "statement-voting" : `statement-voting-${maxNum}`
    return slideType === latestType
  }

  private isLatestPieVotingSlide(slideEl: Element): boolean {
    const wrapper = slideEl.querySelector<HTMLElement>('[data-slide-type^="pie-voting"]')
    if (!wrapper) return false
    const slideType = wrapper.getAttribute("data-slide-type") ?? ""
    const wrappers = this.element.querySelectorAll<HTMLElement>('[data-slide-type^="pie-voting"]')
    let maxNum = 1
    for (const w of wrappers) {
      const type = w.getAttribute("data-slide-type") ?? ""
      const m = type === "pie-voting" ? 1 : parseInt(type.replace("pie-voting-", ""), 10)
      if (!Number.isNaN(m) && m > maxNum) maxNum = m
    }
    const latestType = maxNum === 1 ? "pie-voting" : `pie-voting-${maxNum}`
    return slideType === latestType
  }

  replaceSlides(startIndex: number, length: number, replacementSlides: Element[]) {
    const currentSlideIndex = $(this.element).slick("slickCurrentSlide") as number

    $(this.element).slick("removeSlides", startIndex, length)

    const slidesToAdd: HTMLElement[] = []
    for (const slideContent of replacementSlides) {
      if (
        window.innerWidth < sliderBreakpoint &&
        slideContent.classList.contains("slide-mobile-hide")
      ) {
        continue
      }

      // Need to add two extra divs, due to a bug in SlickSlider
      // See https://github.com/kenwheeler/slick/issues/3324
      const slideDiv = document.createElement("div")
      slideDiv.appendChild(document.createElement("div"))
      slideDiv.firstElementChild.appendChild(slideContent)

      slidesToAdd.push(slideDiv)
    }

    if (slidesToAdd.length > 0) {
      if (startIndex === 0) {
        $(this.element).slick("addSlides", slidesToAdd, true)
      } else {
        $(this.element).slick("addSlides", slidesToAdd, startIndex, true)
      }
    }

    $(this.element).slick("slickGoTo", currentSlideIndex, true)
  }

  async reloadPhotoUploadPages(event) {
    if (!event.params.inviteId) {
      throw new Error("Missing inviteId parameter")
    }

    const response = await get("/participant/photo_upload_fragment_new", {
      query: { signed_book_id: this.bookSignedIdValue, signed_invite_id: event.params.inviteId },
    })
    if (!response.ok) {
      return
    }

    const firstPhotoIndex = this.findSlideIndex(this.element.querySelector(".photo-slides"))
    const photoPageCount = this.element.querySelectorAll(".photo-slides").length

    const template = document.createElement("template")
    template.innerHTML = await response.html
    const newSlides = Array.from(template.content.children)

    this.replaceSlides(firstPhotoIndex, photoPageCount, newSlides)
  }

  async reloadEditFullSizeContentPages(_event) {
    const response = await get(`/fragments/books/${this.bookSignedIdValue}/edit_full_size_content`)
    if (!response.ok) {
      return
    }

    const editFullSizeContentIndex = this.findSlideIndex(
      this.element.querySelector('[data-slide-type="full-size-content"]'),
    )

    const template = document.createElement("template")
    template.innerHTML = await response.html
    const newSlides = Array.from(template.content.children)

    this.replaceSlides(editFullSizeContentIndex, 1, newSlides)
  }

  replaceProfilePages(event) {
    const firstProfilePageIndex = this.findSlideIndex(
      this.element.querySelector(':scope [data-slide-type="about-me"]'),
    )
    const profilePageCount = this.element.querySelectorAll(
      ':scope [data-slide-type="about-me"]',
    ).length

    const doc = event.detail as DocumentFragment

    this.replaceSlides(firstProfilePageIndex, profilePageCount, Array.from(doc.children))
  }

  private removePieAddButtonFromNonLastSlides() {
    const wrappers = this.element.querySelectorAll<HTMLElement>('[data-slide-type^="pie-voting"]')
    const contentSlides: { wrapper: HTMLElement; num: number }[] = []
    for (const w of wrappers) {
      const host = w.querySelector("shadow-page") ?? w.firstElementChild
      const root = (host as Element & { shadowRoot?: ShadowRoot })?.shadowRoot
      if (!root?.querySelector("form.pie-voting-form")) continue
      const type = w.getAttribute("data-slide-type") ?? ""
      const num = type === "pie-voting" ? 1 : parseInt(type.replace("pie-voting-", ""), 10)
      if (!Number.isNaN(num)) contentSlides.push({ wrapper: w, num })
    }
    if (contentSlides.length <= 1) return

    const lastNum = Math.max(...contentSlides.map((s) => s.num))
    const lastWrapper = contentSlides.find((s) => s.num === lastNum)?.wrapper
    if (!lastWrapper) return

    for (const { wrapper } of contentSlides) {
      if (wrapper === lastWrapper) continue
      const host = wrapper.querySelector("shadow-page") ?? wrapper.firstElementChild
      const root = (host as Element & { shadowRoot?: ShadowRoot })?.shadowRoot
      root?.querySelector("form.pie-voting-form .pie-add-question-wrapper")?.remove()
    }
  }

  replaceAllPages(event) {
    const firstSlideIndex = this.findSlideIndex(this.element.querySelector(":scope .slick-slide"))
    const slideCount = this.element.querySelectorAll(":scope .slick-slide").length

    const rawDetail = event.detail
    const doc =
      typeof rawDetail === "function"
        ? (rawDetail() as DocumentFragment)
        : (rawDetail as DocumentFragment)

    this.replaceSlides(firstSlideIndex, slideCount, Array.from(doc.children))

    this.removePieAddButtonFromNonLastSlides()

    const win = window as Window & {
      b4mGoToLastTwoSidedVotingAfterReload?: boolean
      b4mGoToLastStatementVotingAfterReload?: boolean
      b4mGoToLastPieVotingAfterReload?: boolean
    }

    const sliderId = (this.element as HTMLElement).id
    const isAuthorOrCover = sliderId === "book-slider-cover" || sliderId === "book-slider-author"
    if (isAuthorOrCover) {
      if (win.b4mGoToLastTwoSidedVotingAfterReload) {
        delete win.b4mGoToLastTwoSidedVotingAfterReload

        const capturedSliderId = (this.element as HTMLElement).id
        if (!capturedSliderId) return

        const goToLastTwoSidedSlide = () => {
          const sliderEl = document.getElementById(capturedSliderId)
          if (!sliderEl?.classList.contains("slick-initialized")) return

          // Each chunk has two slides (form + empty); we want the form slide of the last chunk.
          // Find highest type number (e.g. two-sided-voting-2 => 2), then first .slick-slide with that type.
          const twoSidedWrappers = sliderEl.querySelectorAll<HTMLElement>(
            '[data-slide-type^="two-sided-voting"]',
          )
          if (twoSidedWrappers.length === 0) return

          let maxNum = 1
          for (const w of twoSidedWrappers) {
            const type = w.getAttribute("data-slide-type") ?? ""
            const m =
              type === "two-sided-voting" ? 1 : parseInt(type.replace("two-sided-voting-", ""), 10)
            if (!Number.isNaN(m) && m > maxNum) maxNum = m
          }
          const targetType = maxNum === 1 ? "two-sided-voting" : `two-sided-voting-${maxNum}`
          const firstSlideWithType = sliderEl.querySelector(
            `.slick-slide [data-slide-type="${targetType}"]`,
          )
          const slideEl = firstSlideWithType?.closest(".slick-slide") as HTMLElement | null
          if (!slideEl) return

          let targetIndex: number
          const slickIndex = slideEl.getAttribute("data-slick-index")
          if (slickIndex !== null && slickIndex !== "") {
            targetIndex = parseInt(slickIndex, 10)
            if (Number.isNaN(targetIndex)) return
          } else {
            const allSlides = sliderEl.querySelectorAll(":scope .slick-slide")
            targetIndex = Array.from(allSlides).indexOf(slideEl)
            if (targetIndex < 0) return
          }

          try {
            ;(
              $(sliderEl) as unknown as { slick: (action: string, ...args: unknown[]) => void }
            ).slick("slickGoTo", targetIndex, true)
          } catch {
            // ignore when slider not ready
          }
        }

        requestAnimationFrame(() => {
          setTimeout(goToLastTwoSidedSlide, 200)
          setTimeout(goToLastTwoSidedSlide, 500)
          setTimeout(goToLastTwoSidedSlide, 1000)
        })
      } else if (win.b4mGoToLastStatementVotingAfterReload) {
        delete win.b4mGoToLastStatementVotingAfterReload

        const capturedSliderId = (this.element as HTMLElement).id
        if (!capturedSliderId) return

        const goToLastStatementSlide = () => {
          const sliderEl = document.getElementById(capturedSliderId)
          if (!sliderEl?.classList.contains("slick-initialized")) return

          const wrappers = sliderEl.querySelectorAll<HTMLElement>(
            '[data-slide-type^="statement-voting"]',
          )
          if (wrappers.length === 0) return

          let maxNum = 1
          for (const w of wrappers) {
            const type = w.getAttribute("data-slide-type") ?? ""
            const m =
              type === "statement-voting" ? 1 : parseInt(type.replace("statement-voting-", ""), 10)
            if (!Number.isNaN(m) && m > maxNum) maxNum = m
          }
          const targetType = maxNum === 1 ? "statement-voting" : `statement-voting-${maxNum}`
          const firstWithType = sliderEl.querySelector(
            `.slick-slide [data-slide-type="${targetType}"]`,
          )
          const slideEl = firstWithType?.closest(".slick-slide") as HTMLElement | null
          if (!slideEl) return

          let targetIndex: number
          const slickIndex = slideEl.getAttribute("data-slick-index")
          if (slickIndex !== null && slickIndex !== "") {
            targetIndex = parseInt(slickIndex, 10)
            if (Number.isNaN(targetIndex)) return
          } else {
            const allSlides = sliderEl.querySelectorAll(":scope .slick-slide")
            targetIndex = Array.from(allSlides).indexOf(slideEl)
            if (targetIndex < 0) return
          }

          try {
            ;(
              $(sliderEl) as unknown as { slick: (action: string, ...args: unknown[]) => void }
            ).slick("slickGoTo", targetIndex, true)
          } catch {
            // ignore
          }
        }

        requestAnimationFrame(() => {
          setTimeout(goToLastStatementSlide, 200)
          setTimeout(goToLastStatementSlide, 500)
          setTimeout(goToLastStatementSlide, 1000)
        })
      } else if (win.b4mGoToLastPieVotingAfterReload) {
        delete win.b4mGoToLastPieVotingAfterReload

        const capturedSliderId = (this.element as HTMLElement).id
        if (!capturedSliderId) return

        const goToLastPieSlide = () => {
          const sliderEl = document.getElementById(capturedSliderId)
          if (!sliderEl?.classList.contains("slick-initialized")) return

          const wrappers = sliderEl.querySelectorAll<HTMLElement>('[data-slide-type^="pie-voting"]')
          if (wrappers.length === 0) return

          let maxNum = 1
          for (const w of wrappers) {
            const type = w.getAttribute("data-slide-type") ?? ""
            const m = type === "pie-voting" ? 1 : parseInt(type.replace("pie-voting-", ""), 10)
            if (!Number.isNaN(m) && m > maxNum) maxNum = m
          }
          const targetType = maxNum === 1 ? "pie-voting" : `pie-voting-${maxNum}`
          const firstWithType = sliderEl.querySelector(
            `.slick-slide [data-slide-type="${targetType}"]`,
          )
          const slideEl = firstWithType?.closest(".slick-slide") as HTMLElement | null
          if (!slideEl) return

          let targetIndex: number
          const slickIndex = slideEl.getAttribute("data-slick-index")
          if (slickIndex !== null && slickIndex !== "") {
            targetIndex = parseInt(slickIndex, 10)
            if (Number.isNaN(targetIndex)) return
          } else {
            const allSlides = sliderEl.querySelectorAll(":scope .slick-slide")
            targetIndex = Array.from(allSlides).indexOf(slideEl)
            if (targetIndex < 0) return
          }

          try {
            ;(
              $(sliderEl) as unknown as { slick: (action: string, ...args: unknown[]) => void }
            ).slick("slickGoTo", targetIndex, true)
          } catch {
            // ignore
          }
        }

        requestAnimationFrame(() => {
          setTimeout(goToLastPieSlide, 200)
          setTimeout(goToLastPieSlide, 500)
          setTimeout(goToLastPieSlide, 1000)
        })
      }
    }
  }
}
